import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../redux/store/store';
import {
  logout,
  navigate,
  selectModule,
  toggleSidebar,
} from '../redux/slice/layoutSlice';
import { logout as clearAuthSession } from '../redux/slice/authSlice';
import { useLogoutMutation, useLogoutAllDevicesMutation } from '../redux/api/platform/tokenApi';
import { builtInModules, canAccessModule } from '../config/module-config';
import {
  getIcon,
  IconBuilding,
  IconChevronDown,
  IconGlobe,
  IconLogout,
  IconMenu,
  IconShield,
  IconUser,
} from '../components/icons';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { ThemeSwitch } from '../components/ThemeSwitch';
import { useT } from '../i18n';
import { Logo } from './Logo';

const initials = (name?: string | null) =>
  (name ?? '')
    .split(' ')
    .map((p) => p.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

// Top bar — adaptation of webapp2's react-admin AppBar: sidebar toggle, brand,
// module switcher, organization, and a user menu.
export const AppBar = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeModuleId, user, organization } = useSelector((s: RootState) => s.layout);
  const [moduleOpen, setModuleOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const t = useT();

  const [logoutMutation] = useLogoutMutation();
  const [logoutAllDevicesMutation] = useLogoutAllDevicesMutation();

  const activeModule = builtInModules.find((m) => m.id === activeModuleId);
  const ActiveIcon = getIcon(activeModule?.icon);
  const anyOpen = moduleOpen || userOpen;

  // Platform owner / broker aren't org members, so they have no "My Organizations".
  const isOrgUser = user.role !== 'platform owner' && user.role !== 'broker';

  const closeAll = () => {
    setModuleOpen(false);
    setUserOpen(false);
  };

  const handleLogout = () => {
    const sessionToken = localStorage.getItem('sessionToken');

    // Best-effort server-side session revoke; never block the local sign-out.
    if (sessionToken) {
      logoutMutation(sessionToken)
        .unwrap()
        .catch(() => undefined);
    }

    dispatch(clearAuthSession());
    dispatch(logout());
    closeAll();
  };

  const handleLogoutAllDevices = async () => {
    if (!window.confirm(t('common.logoutAllDevicesConfirm'))) {
      return;
    }

    // Best-effort server-side revoke of every session; never block the local sign-out.
    try {
      await logoutAllDevicesMutation().unwrap();
    } catch {
      // ignore — still sign out locally below
    }

    dispatch(clearAuthSession());
    dispatch(logout());
    closeAll();
  };

  return (
    <header className="appbar">
      <div className="appbar__left">
        <button
          type="button"
          className="icon-btn"
          aria-label={t('appbar.toggleSidebar')}
          onClick={() => dispatch(toggleSidebar())}
        >
          <IconMenu />
        </button>

        <Logo />

        {/* Module switcher */}
        <div className="dropdown">
          <button
            type="button"
            className="switcher"
            onClick={() => {
              setUserOpen(false);
              setModuleOpen((o) => !o);
            }}
          >
            {ActiveIcon ? <ActiveIcon size={16} /> : null}
            <span className="switcher__label">
              {activeModule ? t(`module.${activeModule.id}`, activeModule.name) : null}
            </span>
            <IconChevronDown size={14} />
          </button>
          {moduleOpen && (
            <div className="dropdown__menu">
              {builtInModules
                .filter((m) => canAccessModule(user.role, m))
                .map((m) => {
                const Icon = getIcon(m.icon);
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`dropdown__item${m.id === activeModuleId ? ' is-active' : ''}`}
                    onClick={() => {
                      dispatch(selectModule(m.id));
                      closeAll();
                    }}
                  >
                    {Icon ? <Icon size={16} /> : null}
                    <span>{t(`module.${m.id}`, m.name)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="appbar__right">
        {/* Platform owner / broker aren't scoped to a single organization, so
            don't show an org badge for them. */}
        {isOrgUser && organization?.name && (
          <span className="appbar__org">
            <IconBuilding size={16} />
            <span>{organization.name}</span>
          </span>
        )}

        {/* User menu */}
        <div className="dropdown dropdown--right">
          <button
            type="button"
            className="avatar-btn"
            aria-label={t('appbar.userMenu')}
            onClick={() => {
              setModuleOpen(false);
              setUserOpen((o) => !o);
            }}
          >
            <span className="topbar__avatar">{initials(user.name)}</span>
          </button>
          {userOpen && (
            <div className="dropdown__menu">
              <div className="dropdown__header">
                <strong>{user.name}</strong>
                <span>{user.email}</span>
              </div>
              <button
                type="button"
                className="dropdown__item"
                onClick={() => {
                  dispatch(navigate('/profile'));
                  closeAll();
                }}
              >
                <IconUser size={16} />
                <span>{t('nav.profile')}</span>
              </button>
              {isOrgUser && (
                <button
                  type="button"
                  className="dropdown__item"
                  onClick={() => {
                    dispatch(navigate('/my-organizations'));
                    closeAll();
                  }}
                >
                  <IconBuilding size={16} />
                  <span>{t('nav.my-organizations')}</span>
                </button>
              )}
              {/* Theme and language live here as well as on the Preferences
                  screen — both get flipped often enough that hunting through a
                  menu for them is friction, and a user stranded in a language
                  they can't read needs a way out that doesn't require
                  navigating through it.

                  The theme toggle deliberately does NOT close the menu: seeing
                  the app change behind it is the feedback, and it is the one
                  control here people flick back and forth. It carries its own
                  label (Dark Mode / Light Mode), so it needs no header. */}
              <ThemeSwitch variant="compact" />
              <div className="dropdown__item dropdown__item--static">
                <IconGlobe size={16} />
                <span>{t('appbar.language')}</span>
              </div>
              <LanguageSwitch variant="compact" onChange={closeAll} />
              <button
                type="button"
                className="dropdown__item"
                onClick={handleLogoutAllDevices}
              >
                <IconShield size={16} />
                <span>{t('common.logoutAllDevices')}</span>
              </button>
              <button
                type="button"
                className="dropdown__item dropdown__item--danger"
                onClick={handleLogout}
              >
                <IconLogout size={16} />
                <span>{t('common.logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {anyOpen && <button type="button" className="dropdown__backdrop" aria-hidden onClick={closeAll} tabIndex={-1} />}
    </header>
  );
};
